import { invoiceStatusColor } from "@lib/helpers/ColorHelper";
import { Invoice, InvoiceStatus } from "@lib/types/Billing";
import { Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { UseMutationProps } from "@lib/types/Api";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CoinPriceTextFormat from "@lib/components/MiniComponents/Others/CoinPriceTextFormat";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TableColumnsProps } from "@lib/types/General";
import { AuthRoles } from "@lib/types";

type Props<T> = TableColumnsProps<T> & {
  openDrawer?: boolean;
  handleSelectInvoice: (invoice: Invoice) => void;
  deleteQuery: UseMutationProps<Invoice>;
  isMobile: boolean;
};

const getColumns = <T extends Invoice>(props: Props<T>): ColumnsType<T> => {
  const {
    deleteQuery,
    handleSelectInvoice,
    messages,
    detailContext,
    isMobile,
    userRole,
  } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "170px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.invoice",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton
          text={`#${r.id}`}
          textToBeCopied={r.id}
          onClick={() => handleSelectInvoice?.(r)}
        />
      ),
      width: "180px",
    },
    {
      title: "common.customerName",
      dataIndex: "customer.id",
      key: "customer.name",
      fixed: "left",
      width: "200px",
      render: (v, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },
    {
      title: "common.amount",
      dataIndex: "amount",
      width: "150px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          showCurrency={r?.currency}
          showCurrencyImage
          bold
          align={isMobile ? "left" : "right"}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.paid",
      dataIndex: "paid",
      width: "140px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          showCurrency={r?.currency}
          bold
          align={isMobile ? "left" : "right"}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.remainingAmount",
      dataIndex: "remainingAmount",
      width: "180px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          showCurrency={r?.currency}
          bold
          align={isMobile ? "left" : "right"}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.status",
      dataIndex: "status",
      render: (value) => (
        <Tag color={invoiceStatusColor(value)}>
          {(messages["enums.status"][value] as string) || value}
        </Tag>
      ),
      width: "120px",
      filters: Object.values(InvoiceStatus).map((value) => ({
        text: (messages["enums.status"][value] as string) || value,
        value: value,
      })),
      filterType: "dropdown",
    },
    // {
    //   title: "common.priority",
    //   dataIndex: "priority",
    //   key: "priority",
    //   render: (priority) => (
    //     <Tag color={invoicePriorityColor(priority)}>
    //       {messages["enums.priority"][priority] as string}
    //     </Tag>
    //   ),
    //   width: "100px",
    //   filters: Object.values(InvoicePriority).map((priority) => ({
    //     text: "enums.priority"][priority] as string,
    //     value: priority,
    //   })),
    //   onFilter: (value, record) => record.priority === value,
    // },
    {
      title: "common.description",
      dataIndex: "description",
      render: (description) => (
        <Typography.Text ellipsis>{description}</Typography.Text>
      ),
      filterType: "search",
      width: "300px",
    },
    {
      title: "common.dueDate",
      dataIndex: "dueDate",
      render: (d) => dayjs(d).fromNow(),
      width: "200px",
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "150px",
      filterType: "search",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "170px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.updatedBy",
      dataIndex: "updatedBy",
      width: "160px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelectInvoice(record)}
          />

          {userRole === AuthRoles.Admin && (
            <ActionButton
              type="delete"
              onClick={() => deleteQuery.mutate(record)}
              noLabel
              itemName="common.invoice"
            />
          )}
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
